import {Text} from '@/components/ui/text/Text'
import {
    StyledAccessRateCard,
    StyledAccessRateCardBody,
    StyledAccessRateCardHeader,
    StyledAccessRateFormRows,
    StyledAccessRateRow,
    StyledList
} from './style'
import {AccessRate, ExternalCostCode} from '@/features/budget/types'
import {externalCodeToLabel} from '../../utils'
import {useTranslation} from '@/translations/i18n'
import {AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, CurrencyEuroIcon, PlusIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {Dispatch, SetStateAction, useState} from 'react'
import {useGetAccessRates} from '../../queries/useGetAccessRates'
import {useUpdateAccessRates} from '../../queries/useUpdateAccessRates'
import {useStoreAccessRates} from '../../queries/useStoreAccessRates'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import dayjs, {formatLocaleDate} from '@/dayjs'
import {Controller, useForm} from 'react-hook-form'
import {AccessRateFormSchema} from '../../formSchemas'
import {zodResolver} from '@hookform/resolvers/zod'
import {InputPrice} from '@/components/commons/input-price/InputPrice'
import {useTheme} from 'styled-components'
import {InputDate} from '@/components/commons/input-date/InputDate'
import {errorHandler} from '@/utilities/helpers'

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR'
    }).format(amount)
}

export const AccessRateCard = ({code}: {code: ExternalCostCode}) => {
    const {t} = useTranslation()
    const [isEditMode, setIsEditMode] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)
    const accessRatesQuery = useGetAccessRates({code})
    const title = `${code} - ${t(externalCodeToLabel[code])}`

    if (isEditMode) {
        return (
            <AccessRateForm
                title={title}
                setIsEditMode={setIsEditMode}
                lastAccessRate={accessRatesQuery.remappedData[0]}
            />
        )
    }

    const dataLength = accessRatesQuery.remappedData.length

    return (
        <StyledAccessRateCard>
            <StyledAccessRateCardHeader>
                <Text size="textLg" weight="500">
                    {title}
                </Text>
                <Button
                    disabled={accessRatesQuery.isPending || accessRatesQuery.isError}
                    variant="secondary"
                    size="sm"
                    onClick={() => setIsEditMode(true)}
                >
                    <PlusIcon />
                    {t('budget:add_access_rate')}
                </Button>
            </StyledAccessRateCardHeader>
            {accessRatesQuery.isPending ? (
                <StyledAccessRateCardBody>
                    <Flexbox justify="center" align="center" fullWidth>
                        <Spinner size={24} />
                    </Flexbox>
                </StyledAccessRateCardBody>
            ) : accessRatesQuery.isError ? (
                <StyledAccessRateCardBody>
                    <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
                </StyledAccessRateCardBody>
            ) : isExpanded ? (
                <StyledList
                    components={{
                        Item: props => <div className="list-item" {...props} />
                    }}
                    endReached={accessRatesQuery.fetchNextPage}
                    data={accessRatesQuery.remappedData}
                    itemContent={index => (
                        <AccessRateRow
                            index={index}
                            isExpanded={isExpanded}
                            setIsExpanded={setIsExpanded}
                            accessRate={accessRatesQuery.remappedData[index]}
                            dataLength={dataLength}
                        />
                    )}
                />
            ) : dataLength >= 1 ? (
                <StyledAccessRateCardBody>
                    <AccessRateRow
                        index={0}
                        isExpanded={isExpanded}
                        setIsExpanded={setIsExpanded}
                        accessRate={accessRatesQuery.remappedData[0]}
                        dataLength={dataLength}
                    />
                </StyledAccessRateCardBody>
            ) : (
                <StyledAccessRateCardBody>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('budget:no_access_rates_found')}
                    </Text>
                </StyledAccessRateCardBody>
            )}
        </StyledAccessRateCard>
    )
}

export const AccessRateRow = ({
    index,
    isExpanded,
    setIsExpanded,
    accessRate,
    dataLength
}: {
    index: number
    isExpanded: boolean
    setIsExpanded: Dispatch<SetStateAction<boolean>>
    accessRate: AccessRate
    dataLength: number
}) => {
    const {t} = useTranslation()

    return (
        <StyledAccessRateRow $isFirst={index == 0}>
            <Flexbox direction="column" gap={1.5}>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('budget:access_rate_rate_label')}
                </Text>
                <Text size="textSm">{formatCurrency(accessRate.rate)}</Text>
            </Flexbox>
            <Flexbox direction="column" gap={1.5}>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('budget:access_rate_start_label')}
                </Text>
                <Text size="textSm">{formatLocaleDate(accessRate.start, 'l')}</Text>
            </Flexbox>
            <Flexbox direction="column" gap={1.5}>
                <Text size="textSm" palette="neutral" shade="500">
                    {t('budget:access_rate_end_label')}
                </Text>
                <Text size="textSm">{accessRate.end ? formatLocaleDate(accessRate.end, 'l') : '-'}</Text>
            </Flexbox>
            {index == 0 && dataLength > 1 && (
                <Button variant="ghost" size="sm" onClick={() => setIsExpanded(isExpanded => !isExpanded)}>
                    {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </Button>
            )}
        </StyledAccessRateRow>
    )
}

export const AccessRateForm = ({
    title,
    setIsEditMode,
    lastAccessRate
}: {
    title: string
    setIsEditMode: Dispatch<SetStateAction<boolean>>
    lastAccessRate: AccessRate
}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const {reset, handleSubmit, control, setValue} = useForm<AccessRateFormSchema>({
        resolver: zodResolver(AccessRateFormSchema)
    })

    const updateAccessRatesMutation = useUpdateAccessRates()
    const storeAccessRatesMutation = useStoreAccessRates()

    const isPending = updateAccessRatesMutation.isPending || storeAccessRatesMutation.isPending

    const onSubmit = handleSubmit(async data => {
        try {
            await updateAccessRatesMutation.mutateAsync({
                urlParams: {id: lastAccessRate.id},
                payload: {
                    code: lastAccessRate.code,
                    rate: lastAccessRate.rate,
                    start: formatLocaleDate(lastAccessRate.start, 'YYYY-MM-DD'),
                    end: formatLocaleDate(data.previousEnd, 'YYYY-MM-DD')
                }
            })

            await storeAccessRatesMutation.mutateAsync({
                payload: {
                    code: lastAccessRate.code,
                    rate: Number(data.rate),
                    start: formatLocaleDate(data.newStart, 'YYYY-MM-DD'),
                    end: null
                }
            })

            setIsEditMode(false)
        } catch (error) {
            errorHandler(error)
        }
    })

    return (
        <StyledAccessRateCard as="form" onSubmit={onSubmit}>
            <StyledAccessRateCardHeader $isEditMode>
                <Text size="textLg" weight="500">
                    {title}
                </Text>
                <Flexbox gap={1}>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            reset()
                            setIsEditMode(false)
                        }}
                        disabled={isPending}
                    >
                        {t('commons:cancel')}
                    </Button>
                    <Button variant="primaryNeutral" size="sm" type="submit" disabled={isPending}>
                        {t('commons:confirm')}
                        {isPending && <Spinner size={16} />}
                    </Button>
                </Flexbox>
            </StyledAccessRateCardHeader>
            <StyledAccessRateCardBody>
                <StyledAccessRateFormRows>
                    <Controller
                        control={control}
                        name="rate"
                        render={({field, fieldState}) => (
                            <InputPrice
                                getInputRef={field.ref}
                                label={t('budget:access_rate_rate_label')}
                                placeholder={'0,00'}
                                name="rate"
                                value={field.value}
                                onValueChange={values => {
                                    field.onChange(values.value)
                                }}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={false}
                                errorMessage={fieldState.error?.message}
                                typeIcon={<CurrencyEuroIcon size={16} color={palette.neutral[500]} />}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="newStart"
                        render={({field, fieldState}) => (
                            <InputDate
                                label={t('budget:access_rate_start_label')}
                                placeholder={'dd/mm/yyyy'}
                                name="newStart"
                                disableBeforeDate={dayjs(lastAccessRate.start).add(2, 'day').toDate()}
                                fullWidth
                                errorMessage={fieldState.error?.message}
                                value={field.value ? new Date(field.value) : undefined}
                                onChange={value => {
                                    field.onChange(value ? value.toISOString() : '')
                                    setValue(
                                        'previousEnd',
                                        value ? dayjs(value).subtract(1, 'day').toISOString() : '',
                                        {shouldValidate: true}
                                    )
                                }}
                            />
                        )}
                    />
                    <div />
                    <InputPrice
                        disabled
                        label={t('budget:outpatient_amount_label')}
                        placeholder={'0,00'}
                        name="outpatientAmount"
                        value={lastAccessRate.rate}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        allowNegative={false}
                        typeIcon={<CurrencyEuroIcon size={16} color={palette.neutral[500]} />}
                    />
                    <InputDate
                        disabled
                        label={t('budget:access_rate_start_label')}
                        placeholder={'dd/mm/yyyy'}
                        fullWidth
                        value={lastAccessRate.start ? new Date(lastAccessRate.start) : undefined}
                    />
                    <Controller
                        control={control}
                        name="previousEnd"
                        render={({field, fieldState}) => (
                            <InputDate
                                label={t('budget:access_rate_end_label')}
                                placeholder={'dd/mm/yyyy'}
                                name="previousEnd"
                                disableBeforeDate={dayjs(lastAccessRate.start).add(1, 'day').toDate()}
                                fullWidth
                                errorMessage={fieldState.error?.message}
                                value={field.value ? new Date(field.value) : undefined}
                                onChange={value => {
                                    field.onChange(value ? value.toISOString() : '')
                                    setValue('newStart', value ? dayjs(value).add(1, 'day').toISOString() : '', {
                                        shouldValidate: true
                                    })
                                }}
                            />
                        )}
                    />
                </StyledAccessRateFormRows>
            </StyledAccessRateCardBody>
        </StyledAccessRateCard>
    )
}
